import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, MagnifyingGlass, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { QboCustomerLinker } from "@/components/QboCustomerLinker";
import { api, formatApiErrorDetail } from "@/lib/api";

const selectCls =
  "w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-indigo-400/50 disabled:opacity-50";

// Integrations → QuickBooks → "Customer matching" tab: rules for how accounts meet
// QuickBooks customers, plus a picker to link any account by hand.
export const QboCustomers = ({ connected }) => {
  const [autoMatch, setAutoMatch] = useState(true);
  const [autoSync, setAutoSync] = useState(true);
  const [matchStrategy, setMatchStrategy] = useState("name_company_email");
  const [saving, setSaving] = useState(false);
  const [appQ, setAppQ] = useState("");
  const [appResults, setAppResults] = useState([]);
  const [selApp, setSelApp] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/qbo/mapping");
      setAutoMatch(d.auto_match_customers !== false);
      setAutoSync(d.auto_sync_customers !== false);
      setMatchStrategy(d.match_strategy || "name_company_email");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load settings");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/qbo/mapping", {
        auto_match_customers: autoMatch,
        auto_sync_customers: autoSync,
        match_strategy: matchStrategy,
      });
      toast.success("Customer matching saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    }
    setSaving(false);
  };

  const searchApp = async (v) => {
    setAppQ(v);
    if (v.trim().length < 2) {
      setAppResults([]);
      return;
    }
    try {
      const { data: rows } = await api.get(`/customers?q=${encodeURIComponent(v.trim())}`);
      setAppResults((rows || []).slice(0, 8));
    } catch {
      setAppResults([]);
    }
  };

  return (
    <div
      className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900 p-6"
      data-testid="qbo-customers-panel"
    >
      <h3 className="text-base font-semibold text-zinc-100">Customer matching</h3>
      <p className="text-sm text-zinc-500">
        How your accounts meet QuickBooks customers. Existing links are always reused; when
        nothing matches, a new QuickBooks customer is created so nothing is left unsynced.
      </p>

      {!connected && (
        <div
          className="mt-4 flex items-center gap-2 rounded-xl border border-amber-500/25 bg-amber-500/10 p-3 text-sm text-amber-200/90"
          data-testid="qbo-customers-connect-note"
        >
          <Warning size={16} weight="bold" />
          Connect QuickBooks to search its customers and sync accounts.
        </div>
      )}

      <div className="mt-5 space-y-3 rounded-xl border border-zinc-800 bg-zinc-950/40 p-4">
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input
            data-testid="qbo-map-autosync"
            type="checkbox"
            checked={autoSync}
            onChange={(e) => setAutoSync(e.target.checked)}
            className="h-4 w-4 accent-indigo-500"
          />
          Sync every new account to QuickBooks automatically (match, or create)
        </label>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input
            data-testid="qbo-map-automatch"
            type="checkbox"
            checked={autoMatch}
            onChange={(e) => setAutoMatch(e.target.checked)}
            className="h-4 w-4 accent-indigo-500"
          />
          Look for an existing QuickBooks customer before creating one
        </label>
        {autoMatch && (
          <div>
            <span className="mb-1 block text-[10px] uppercase tracking-widest text-zinc-500">
              Matching rule
            </span>
            <select
              data-testid="qbo-map-match-strategy"
              value={matchStrategy}
              onChange={(e) => setMatchStrategy(e.target.value)}
              className={`${selectCls} max-w-sm`}
            >
              <option value="exact">Match by name only</option>
              <option value="name_company_email">Match by name, company or email</option>
            </select>
          </div>
        )}
        <p className="text-xs text-zinc-500">
          Invoices always sync when they are raised; the customer on them is matched or created
          the same way. You can also do this per account from its page (QuickBooks card).
        </p>
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <div>
          <p className="mb-1 text-[10px] uppercase tracking-widest text-zinc-500">
            Find one of your accounts
          </p>
          <div className="relative">
            <MagnifyingGlass
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <input
              data-testid="qbo-cust-app-search"
              value={appQ}
              onChange={(e) => searchApp(e.target.value)}
              placeholder="Search account name…"
              className={`${selectCls} pl-9`}
            />
          </div>
          {appResults.length > 0 && (
            <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950">
              {appResults.map((r) => (
                <button
                  key={r.id}
                  data-testid={`qbo-cust-app-result-${r.id}`}
                  onClick={() => {
                    setSelApp({ id: r.id, name: r.name });
                    setAppResults([]);
                    setAppQ(r.name);
                  }}
                  className="block w-full truncate px-3 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-800"
                >
                  {r.name}
                  {r.company ? <span className="text-zinc-600"> · {r.company}</span> : null}
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="mb-1 text-[10px] uppercase tracking-widest text-zinc-500">
            Link to QuickBooks customer
          </p>
          {selApp ? (
            <QboCustomerLinker
              customerId={selApp.id}
              customerName={selApp.name}
              connected={connected}
              compact
            />
          ) : (
            <p className="text-sm text-zinc-600">Pick an account on the left first.</p>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          data-testid="qbo-customers-save"
          onClick={save}
          disabled={saving}
          className="gap-2 bg-emerald-500 text-zinc-950 hover:bg-emerald-400 disabled:opacity-40"
        >
          <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save matching rules"}
        </Button>
      </div>
    </div>
  );
};
